"use client"

import * as React from "react"
import {
  Home,
  List,
  MapIcon,
  Camera,
  Send,
  Shield,
  BarChart3,
  Wind,
  Activity,
} from "lucide-react"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
} from "@/components/ui/sidebar"
import { NavSecondary } from "@/components/nav-secondary"
import { useCachedQuery } from "@/hooks/use-cached-query"
import { useAuth } from "@workos-inc/authkit-react"
import { api } from "../../convex/_generated/api"
import { QueryErrorBoundary } from "./query-error-boundary"
import { useConvexAuth, useQuery } from "convex/react"

const navMain = [
  {
    title: "Dashboard",
    url: "/",
    icon: Home,
  },
  {
    title: "Advanced Dashboard",
    url: "/advanced-dashboard",
    icon: BarChart3,
  },
  {
    title: "Long-term forecast",
    url: "/forecast",
    icon: Wind,
  },
  {
    title: "Submit Report",
    url: "/submit-report",
    icon: Send,
  },
  {
    title: "Latest Submissions",
    url: "/latest-submissions",
    icon: List,
  },
  {
    title: "Map",
    url: "/map",
    icon: MapIcon,
  },
  {
    title: "Photography guide",
    url: "/photography",
    icon: Camera,
  },
]



const adminNavItems = [
  {
    title: "Admin",
    url: "/admin",
    icon: Shield,
  },
  {
    title: "Status",
    url: "/status",
    icon: Activity,
  },
]

// Component to safely check if user is admin - wraps the query in error boundary
function AdminCheck({ children }: { children: (isAdmin: boolean) => React.ReactNode }) {
  return (
    <QueryErrorBoundary fallback={<>{children(false)}</>}>
      <AdminCheckContent>{children}</AdminCheckContent>
    </QueryErrorBoundary>
  )
}

function AdminCheckContent({ children }: { children: (isAdmin: boolean) => React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? "anonymous";
  const { data: adminResult } = useCachedQuery(
    api.users.isAdmin,
    {},
    {
      table: 'userData',
      ttl: 15 * 60 * 1000, // Admin status rarely changes, cache for 15 minutes
      cacheKey: `user-is-admin:${userId}`,
    }
  );
  // If query succeeds but returns undefined/null/false, default to false
  const isAdmin = adminResult === true;

  return <>{children(isAdmin)}</>
}

function NavSecondaryWithAdmin({ isAdmin }: { isAdmin: boolean }) {
  const navSecondary = React.useMemo(() => {
    if (isAdmin) {
      return adminNavItems
    }
    return []
  }, [isAdmin])

  return <NavSecondary items={navSecondary} className="mt-auto" />
}

function AppSidebarContent({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { isAuthenticated } = useConvexAuth()
  const canAccessForecast = useQuery(
    api.users.canAccessAdvancedForecast,
    isAuthenticated ? undefined : "skip"
  )
  const subscription = useQuery(
    api.polar.getCurrentSubscription,
    isAuthenticated ? {} : "skip"
  )
  const hasProAccess = canAccessForecast === true || (subscription !== undefined && subscription !== null)
  const navItems = React.useMemo(() => {
    return navMain.filter((item) => {
      if (item.url !== "/forecast") {
        return true
      }
      return isAuthenticated && hasProAccess
    })
  }, [isAuthenticated, hasProAccess])

  return (
    <Sidebar
      className="top-(--header-height) h-[calc(100svh-var(--header-height))]!"
      {...props}
    >
      <SidebarContent>
        <NavMain items={navItems} />
        <AdminCheck>
          {(isAdmin) => <NavSecondaryWithAdmin isAdmin={isAdmin} />}
        </AdminCheck>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // Don't wrap entire sidebar in error boundary - let individual components handle their own errors
  // NavUser has its own error boundary, AdminCheck handles admin query errors
  return <AppSidebarContent {...props} />
}
